import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import lodashDebounce from 'lodash.debounce'

import StyledWrapper from './StyledWrapper'
import Header from 'components_v2/Header'
import { useAuthFeedback } from 'components_v2/AuthProviders'
import { ToastContainer } from 'ui/bend'

import './StyledHeader.scss'

/*
This component is used as a shim to replace the old transparent header/nav on pages that have not been re-designed/Reactified yet.
However, sticky positioning isn't compatible with the old layout. Use fixed positioning here instead.
This is somewhat convoluted because the alert bar is loaded via an API call after page load.

We place <ToastContainer/> and useAuthFeedback() here so that we can guarantee they will fire only once on legacy pages.
If we placed them in <StyledWrapper/>, <ThemeWrapper/> or <EntryProviders/> they would fire multiple times per legacy page (Header, Footer, and any other shimmed components)
*/

const StyledHeader = ({ user, ...rest }) => {
  const [state, setState] = useState({
    navbarOffset: 0,
    alertLoaded: false
  })

  useAuthFeedback()

  useEffect(() => {
    const reactNavbar = document.getElementById('ReactNavbar')
    const reactHeaderSpacer = document.getElementById('ReactHeader__spacer')

    reactHeaderSpacer.style.height = `${reactNavbar.offsetHeight}px`

    const fixNavbar = (fixed) => {
      if (fixed) {
        reactNavbar.classList.add('ReactNavbar--fixed')
        reactHeaderSpacer.style.display = 'block'
      } else {
        reactNavbar.classList.remove('ReactNavbar--fixed')
        reactHeaderSpacer.style.display = 'none'
      }
    }

    // Once the navbar reaches the top of the page (via scrolling down), we set its position to fixed
    const handleScroll = () => {
      fixNavbar(window.pageYOffset >= state.navbarOffset)
    }

    // The alert_bar_status event is emitted after the alert bar API call finishes
    // and after the alert bar is dismissed (see AlertBar.js)
    const handleAlertBarStatus = (event) => {
      const alertVisible = event.detail

      setState({
        navbarOffset: alertVisible ? reactNavbar.offsetTop : 0,
        alertLoaded: true
      })
    }

    //
    // Set up event listeners
    //
    const debouncedScroll = lodashDebounce(handleScroll, 50, {
      leading: true,
      maxWait: 50
    })

    if (state.navbarOffset > 0) {
      // Dynamically adjust fixed positioning for navbar if it has an offset (alert bar is visible)
      window.addEventListener('scroll', debouncedScroll)
    } else if (state.alertLoaded) {
      // No offset (alert bar is hidden) -> make navbar fixed (and don't listen to scroll event)
      // Only after we know either a) there is no alert or b) alert has been dismissed
      // Otherwise the fixed navbar will be covering the alert when it transitions in
      fixNavbar(true)
    }

    // Listen for when the alert bar is shown/hidden so we can update state.navbarOffset
    window.addEventListener('alert_bar_status', handleAlertBarStatus)

    return () => {
      window.removeEventListener('scroll', debouncedScroll)
      window.removeEventListener('alert_bar_status', handleAlertBarStatus)
    }
  }, [state])

  return (
    <StyledWrapper user={user}>
      <Header {...rest} />
      <ToastContainer />
      <div id='ReactHeader__spacer'></div>
    </StyledWrapper>
  )
}

StyledHeader.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired
  })
}

export default StyledHeader
